import type { SelectPaginatedData } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface TutorsListApiOptions {
  query?: string
  page?: number
  signal?: AbortSignal
  preselected?: number[]
  active?: boolean
  paid?: boolean
}

export const useTutorsListApi = async (
  opts: TutorsListApiOptions = {},
): Promise<SelectPaginatedData | null> => {
  const { query = '', page = 1, signal, preselected, active, paid } = opts
  const { $axios } = useNuxtApp()

  const toast = useToast()

  const url = useAdminRoutes('admin.tutors.list')

  if (!url) {
    toast.error('Unexpected error. Invalid request URL.')
    return null
  }

  try {
    const params: Record<string, string | number> = {
      page,
      per_page: 25,
    }
    if (query !== '') params.q = query
    // The API applies these only when true; false would read as "no filter" anyway.
    if (active) params.active = 1
    if (paid) params.paid = 1
    if (preselected !== undefined) {
      preselected.forEach((id, idx) => {
        params[`preselected[${idx}]`] = id
      })
    }

    const { data } = await $axios.get(url, { params, signal })
    const paginatedData: SelectPaginatedData | null = data?.data || null

    if (!data.success || !paginatedData) {
      toast.error('Unexpected error.')
      return null
    }

    return paginatedData
  }
  catch (err: unknown) {
    if ((err as Error)?.name === 'CanceledError') return null
    toast.error(getAxiosErrorMessage(err))
    return null
  }
}
